import { PropsWithChildren, useEffect, useState } from 'react';
import { FlagProvider, IConfig, useFlagsStatus } from '@unleash/proxy-client-react';
import { useAponoAuth } from '@common/ui/Authenticated';
import { Loader } from '@common/ui/Loader';
import { Backdrop } from '@mui/material';
import { useLocalStorage } from 'usehooks-ts';
import ErrorPage from '@common/pages/ErrorPage';
import { AppTheme } from '@common/ui/AppTheme';

const getDefaultConfig = () => ({
  url: `${window.location.protocol}//${window.location.host}/api/v1/feature-flags`,
  refreshInterval: 0,
  disableMetrics: true,
  appName: 'default',
  environment: 'default',
  clientKey: 'proxy-client-key',
});

function FeatureFlagsProvider({ children }: PropsWithChildren) {
  const { user, accessToken } = useAponoAuth();

  const config: IConfig = {
    ...getDefaultConfig(),
    clientKey: `Bearer ${accessToken}`,
    context: { userId: user?.id },
  };

  return (
    <FlagProvider config={config}>
      <FeatureFlagsWrapper>{children}</FeatureFlagsWrapper>
    </FlagProvider>
  );
}

const LS_KEY = 'unauthorized-reload-count';
const MAX_UNAUTHORIZED_RELOADS = 1;

function FeatureFlagsWrapper({ children }: PropsWithChildren) {
  const [unauthorizedReloadCount, setUnauthorizedReloadCount] = useLocalStorage(LS_KEY, 0);
  const [showUnauthorizedError, setShowUnauthorizedError] = useState(false);
  const [showOtherError, setShowOtherError] = useState(false);

  const { flagsReady, flagsError } = useFlagsStatus();

  useEffect(
    () => {
      if (flagsReady) {
        setShowUnauthorizedError(false);
        setShowOtherError(false);
        setUnauthorizedReloadCount(0);
        return;
      }

      if (!flagsError) return;

      const code = flagsError.code;
      if (code === 401) {
        if (unauthorizedReloadCount < MAX_UNAUTHORIZED_RELOADS) {
          setUnauthorizedReloadCount(unauthorizedReloadCount + 1);
          window.location.reload();
          return;
        }

        if (unauthorizedReloadCount >= MAX_UNAUTHORIZED_RELOADS) {
          setShowUnauthorizedError(true);
        }
      } else {
        setShowOtherError(true);
      }
    },
    // Very important not to put unauthorizedReloadCount as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flagsReady, flagsError],
  );

  if (showUnauthorizedError) {
    return (
      <AppTheme>
        <Backdrop open invisible>
          <ErrorPage
            errorCode={401}
            errorMessage="Failed to authorize the user. Please contact administrator."
            showLogo
            showHomeButton={false}
          />
        </Backdrop>
      </AppTheme>
    );
  }

  if (showOtherError) {
    return (
      <AppTheme>
        <Backdrop open invisible>
          <ErrorPage
            errorCode={flagsError.code}
            errorMessage={flagsError.message || 'Internal server error. Please contact administrator'}
            showLogo
            showHomeButton={false}
          />
        </Backdrop>
      </AppTheme>
    );
  }

  if (!flagsReady) return <Loader absolute />;

  return <>{children}</>;
}

export default FeatureFlagsProvider;
